import { useState, useRef, useEffect } from 'react';
import { styled, alpha } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { PickersDay as MuiPickersDay } from '@mui/x-date-pickers/PickersDay';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import Moment from 'moment';
import MuiTextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CalendarIcon from '../icons/CalendarIcon';
import ChevronLeftIcon from '../icons/ChevronLeftIcon';
import ChevronRightIcon from '../icons/ChevronRightIcon';
import { hairline } from '../styles';

// ── `pill`: the consumer product's date control ────────────────────────────────
// Ported from hitch-web's date affordance (HeroDatePill + the `.embedded-calendar`
// react-calendar theme) so picking a date in admin looks and behaves like picking one
// on hitch.com: a fully-rounded chip with a leading calendar glyph and a friendly
// "Monday, Aug 17" label — not an MM/DD/YYYY text field — opening a soft-cornered
// calendar of circular days that commits on tap.
//
// hitch-web is light-only and hardcodes its palette (#f9f9f9 fill, #e5e5e5 border,
// #222 selected, #717171 secondary text). Admin has a dark mode, so each of those is
// mapped to the theme token that resolves to the consumer value in light mode rather
// than pasted in literally.
//
// Form date pickers omit `pill` and keep the default outlined, typeable field —
// typing beats tapping when you're entering a date you already know.
const datePillSx = {
  '& .MuiOutlinedInput-root': {
    height: 36,
    // Consumer uses a full-round chip (rounded-[24px] on a 36px control).
    borderRadius: '999px',
    fontSize: '14px',
    fontWeight: 500,
    color: 'text.primary',
    cursor: 'pointer',
    backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#F9F9F9'),
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
    '& fieldset': { border: (theme) => `1px solid ${hairline(theme)}` },
    '&:hover': { backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#ECECEC') },
    '&:hover fieldset': { border: (theme) => `1px solid ${hairline(theme)}` },
    '&.Mui-focused fieldset': { border: (theme) => `1px solid ${theme.palette.text.inactive}` },
  },
  // The field is read-only in this variant (the calendar is the only way in), so the
  // text is a label rather than an input: no caret, and the whole chip is the target.
  '& .MuiOutlinedInput-input': {
    padding: '0 14px 0 0',
    cursor: 'pointer',
    textOverflow: 'ellipsis',
  },
  '& .MuiInputAdornment-positionStart': { marginLeft: '14px', marginRight: '6px' },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': { fontSize: 16, color: 'text.secondary' },
};

const CustomPickersDay = styled(MuiPickersDay, {
  shouldForwardProp: (prop) => prop !== 'dayIsBetween' && prop !== 'isFirstDay' && prop !== 'isLastDay',
})(({ theme, dayIsBetween, isFirstDay, isLastDay }) => ({
  ...(dayIsBetween && {
    borderRadius: 0,
    backgroundColor: alpha(theme.palette.primary.main, 0.85),
    color: theme.palette.common.white,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.dark,
    },
  }),
  ...(isFirstDay && {
    backgroundColor: theme.palette.primary.main,
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
  }),
  ...(isLastDay && {
    backgroundColor: theme.palette.primary.main,
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
    borderColor: theme.palette.primary.main,
  }),
}));

// The consumer calendar, matched rule for rule against hitch-web's
// `.embedded-calendar` block: 20px corners on a hairline border, a medium-weight
// centred month label that is NOT a drill-up button, 36px circular chevrons that fill
// grey on hover, quiet single-letter weekday headers, and 42px circular days.
const calendarPaperSx = (theme) => ({
  borderRadius: '20px',
  border: `1px solid ${hairline(theme)}`,
  boxShadow: theme.palette.mode === 'dark' ? '0 8px 28px rgba(0,0,0,0.6)' : '0 2px 20px rgba(0,0,0,0.10)',
  marginTop: '8px',
  padding: '8px',
  '& .MuiPickersCalendarHeader-root': { marginTop: '4px', paddingLeft: '12px', paddingRight: '8px' },
  '& .MuiPickersCalendarHeader-label': { fontWeight: 500, fontSize: '16px', lineHeight: '24px' },
  // `views={['day']}` already removes the drill-up, so hide its leftover chevron.
  '& .MuiPickersCalendarHeader-switchViewButton': { display: 'none' },
  '& .MuiPickersArrowSwitcher-button': {
    width: 36,
    height: 36,
    borderRadius: '999px',
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#F0F0F0',
      color: theme.palette.text.primary,
    },
    '&.Mui-disabled': { opacity: 0.3 },
  },
  '& .MuiDayPicker-weekDayLabel, & .MuiDayCalendar-weekDayLabel': {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    fontSize: '12px',
    textTransform: 'none',
  },
});

// 42px circles, 14px/500 type, soft grey hover, solid dark selected circle that stays
// dark on hover, thin-ring today, light grey disabled — the consumer day cell.
const daySx = {
  width: 42,
  height: 42,
  fontSize: '14px',
  fontWeight: 500,
  // MUI defaults a day's fill to `background.paper`. In dark mode the popper's Paper
  // carries an elevation overlay that lightens it while the days don't, so every day
  // rendered as a dark disc on a lighter sheet. The consumer tile is transparent until
  // hovered or selected, which is also what makes the grid read as a calendar rather
  // than 31 buttons.
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#F0F0F0'),
  },
  '&.MuiPickersDay-today': {
    borderColor: (theme) => theme.palette.text.primary,
  },
  '&.Mui-disabled': {
    color: (theme) => theme.palette.text.inactive,
  },
  '&.Mui-selected': {
    backgroundColor: (theme) => theme.palette.text.primary,
    color: (theme) => theme.palette.background.paper,
    fontWeight: 500,
    '&:hover, &:focus': {
      backgroundColor: (theme) => theme.palette.text.primary,
    },
  },
};

// hitch-web labels a chosen date "Monday, Aug 17" (localCalendarLabel) and drops the
// year, which is fine for a booking window that's only 60 days deep. Admin is not:
// ops page back and forth across months and a bare "Feb 3" with no year is a
// genuinely ambiguous thing to act on. So the consumer format is used as-is inside the
// current year and grows a year only when the date leaves it.
const pillFormat = (value) => {
  const m = value ? Moment(value) : null;
  if (m && m.isValid() && m.year() !== Moment().year()) return 'ddd, MMM D, YYYY';
  return 'dddd, MMM D';
};

const DatePicker = ({ label, value, onChange, fullWidth, moment = false, startDate, endDate, sx, size, pill = false, ...props }) => {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const textField = useRef(null);

  const _onChange = (value) => {
    if (moment) {
      return onChange(value);
    }
    onChange && onChange({ target: { value: value.format('YYYY-MM-DD') } });
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      const input = textField.current ? textField.current.querySelector('input') : null;
      const key = event.key && event.key.toLowerCase();
      if (key === 'enter' && input && event.target && input.id === event.target.id) {
        setOpen(true);
      }
    };

    if (focused) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [focused]);

  const renderWeekPickerDay = (date, selectedDates, pickersDayProps) => {
    if (!value || (!startDate && !endDate)) {
      return <MuiPickersDay {...pickersDayProps} sx={daySx} />;
    }

    const start = Moment(startDate || value);
    const end = Moment(endDate || value);

    const dayIsBetween = date.isBetween(start, end, null, '[]');
    const isFirstDay = date.isSame(start, 'day');
    const isLastDay = date.isSame(end, 'day');

    return (
      <CustomPickersDay
        {...pickersDayProps}
        disableMargin
        sx={daySx}
        dayIsBetween={dayIsBetween}
        isFirstDay={isFirstDay}
        isLastDay={isLastDay}
      />
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <MuiDatePicker
        open={open}
        label={label}
        inputFormat={pill ? pillFormat(value) : 'MM/DD/YYYY'}
        // Consumer parity: one letter per weekday (S M T W T F S), a month-only view
        // so the label can't drill up into a year grid, and the consumer's chevrons.
        {...(pill && {
          // Force the ANCHORED POPOVER at every width. Left to itself, MUI swaps to its
          // mobile variant on a narrow screen: a centred modal with a "SELECT DATE"
          // toolbar, a keyboard-entry pencil, and OK/Cancel buttons. hitch-web has no
          // such mode — the same popover drops under the chip on a phone and a tap on a
          // day commits and closes — so the responsive swap is exactly what breaks
          // parity here, and desktop mode is what restores it.
          desktopModeMediaQuery: '(min-width: 0px)',
          views: ['day'],
          dayOfWeekFormatter: (day) => day.charAt(0),
          components: { LeftArrowIcon: ChevronLeftIcon, RightArrowIcon: ChevronRightIcon },
        })}
        value={value}
        onChange={_onChange}
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false);
        }}
        renderDay={renderWeekPickerDay}
        PaperProps={{ sx: calendarPaperSx }}
        renderInput={({ inputRef, inputProps, InputProps }) => {
          return (
            <MuiTextField
              label={label}
              fullWidth={fullWidth}
              size={size}
              sx={{ ...(pill ? datePillSx : {}), ...sx }}
              ref={textField}
              inputRef={inputRef}
              onClick={pill ? () => !open && setOpen(true) : undefined}
              inputProps={{
                ...inputProps,
                // The consumer chip isn't typeable — the calendar is the only way to
                // change the date. Read-only (not `disabled`) keeps it focusable, so
                // the existing Enter-to-open shortcut still works.
                ...(pill && { readOnly: true }),
                onClick: () => !open && setOpen(true),
                onFocus: () => setFocused(true),
                onBlur: () => setFocused(false),
              }}
              InputProps={{
                ...InputProps,
                // Consumer puts the glyph before the label; MUI's default trailing
                // icon-button is dropped since the whole chip already opens it.
                ...(pill && {
                  endAdornment: null,
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon />
                    </InputAdornment>
                  ),
                }),
              }}
            />
          );
        }}
        {...props}
      />
    </LocalizationProvider>
  );
};

export default DatePicker;
